import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  CalendarEvent,
  CalendarEventCategory,
  CalendarMonthDay,
  CalendarSettings,
  CalendarSlotClickEvent,
  DEFAULT_CALENDAR_SETTINGS,
} from '../../models/calendar.types';
import { formatTimeRange, generateMonthGrid } from '../../utils/calendar-date.utils';

@Component({
  selector: 'aura-calendar-month-view',
  imports: [MatTooltipModule],
  templateUrl: './calendar-month-view.component.html',
  host: {
    class: 'flex flex-col flex-1 min-h-0 w-full h-full',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarMonthViewComponent {
  readonly currentDate = input.required<Date>();
  readonly events = input<CalendarEvent[]>([]);
  readonly settings = input<CalendarSettings>(DEFAULT_CALENDAR_SETTINGS);

  readonly eventClick = output<CalendarEvent>();
  readonly slotClick = output<CalendarSlotClickEvent>();

  readonly dayLabels = computed(() => {
    const s = this.settings();
    if (s.firstDayOfWeek === 0) {
      return s.showWeekends
        ? ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']
        : ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven'];
    }
    return s.showWeekends
      ? ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']
      : ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven'];
  });

  readonly days = computed<CalendarMonthDay[]>(() =>
    generateMonthGrid(
      this.currentDate(),
      this.events(),
      this.settings().firstDayOfWeek,
      this.settings().showWeekends,
    ),
  );

  onDayClick(day: CalendarMonthDay, event?: Event): void {
    // Si le clic ne vient pas d'un badge d'événement
    if (event) {
      const target = event.target as HTMLElement | null;
      if (target?.closest?.('[data-event-pill]')) {
        return;
      }
    }
    this.slotClick.emit({ date: day.date });
  }

  onEventClick(evt: CalendarEvent, event?: Event): void {
    event?.stopPropagation?.();
    this.eventClick.emit(evt);
  }

  getTimeFormatted(evt: CalendarEvent): string {
    if (evt.allDay) return 'Journée';
    return formatTimeRange(evt.startAt, evt.endAt);
  }

  getCategoryClasses(category?: CalendarEventCategory): string {
    switch (category) {
      case 'jade':
        return 'bg-jade-3 text-jade-11 border-jade-6 hover:bg-jade-4';
      case 'amber':
        return 'bg-amber-3 text-amber-11 border-amber-6 hover:bg-amber-4';
      case 'ruby':
        return 'bg-ruby-3 text-ruby-11 border-ruby-6 hover:bg-ruby-4';
      case 'violet':
        return 'bg-violet-3 text-violet-11 border-violet-6 hover:bg-violet-4';
      case 'indigo':
        return 'bg-indigo-3 text-indigo-11 border-indigo-6 hover:bg-indigo-4';
      case 'primary':
      default:
        return 'bg-primary-3 text-primary-11 border-primary-6 hover:bg-primary-4';
    }
  }

  getDotColor(category?: CalendarEventCategory): string {
    switch (category) {
      case 'jade':
        return 'bg-jade-9';
      case 'amber':
        return 'bg-amber-9';
      case 'ruby':
        return 'bg-ruby-9';
      case 'violet':
        return 'bg-violet-9';
      case 'indigo':
        return 'bg-indigo-9';
      case 'primary':
      default:
        return 'bg-primary-9';
    }
  }
}
